'use client';

/**
 * LocationAutocomplete — type a place, pick it from suggestions as you type.
 * Used by the explorer trip panel (via AddStopByPlace).
 *
 * Two sources, merged, de-duplicated and ranked (utils/placeSuggest):
 *  • /api/search — our own index: towns, regions and campsites. Fast and
 *    unmetered, so it runs on a short debounce while you type.
 *  • /api/geocode — Nominatim, for parks, localities and addresses we don't
 *    index. Its usage policy caps us near 1 request/sec (cached an hour
 *    server-side), so it only fires after a longer typing pause, never per
 *    keystroke, and an in-flight lookup is aborted when you keep typing.
 *
 * Ranking: towns before regions, and results near the trip (`near`) before
 * the same name on the other side of the country.
 *
 * Not Google Places: an API-restriction change on the shared web key once took
 * every location field offline at once. This path has no Google dependency.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { getCountryCode } from '../lib/brand';
import { fetchSearch } from '../lib/searchApi';
import { geocodePlace, MIN_GEOCODE_QUERY } from '../utils/geocodeApi';
import { nearestOnPath } from '../utils/routeSnap';
import { formatAddedDrive, formatAddedKm, withRoadDistance, type PlacementInfo } from '../utils/tripInsert';
import {
  fromGeocode,
  fromSearchCards,
  mergeSuggestions,
  type Anchor,
  type PlaceIcon,
  type PlaceSuggestion,
} from '../utils/placeSuggest';

export interface ResolvedLocation {
  label: string;
  lat: number;
  lng: number;
  /** Set when the pick is a campsite — the stop links to that listing. */
  entityId?: number;
  kind?: 'place' | 'campsite';
}

interface LocationAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: ResolvedLocation | null;
  onChange: (loc: ResolvedLocation | null) => void;
  allowMyLocation?: boolean;
  /** Offer campsites as well as places (default true). */
  includeCampsites?: boolean;
  /** Points the trip already covers — nearby results rank first. */
  near?: Anchor[];
  autoFocus?: boolean;
  /** Esc with the list already closed (e.g. close an inline "add here" box). */
  onEscape?: () => void;
  /** Accessible name when the visible label lives outside this component. */
  ariaLabel?: string;
  /** Keep the label for screen readers but don't draw it (the placeholder
   *  says the same thing, and every pixel above the stop list counts). */
  hideLabel?: boolean;
  /** Marker inside the field's left edge (e.g. the trip's start/end pin);
   *  replaces the search glyph. */
  leadingIcon?: React.ReactNode;
  /**
   * A secondary way in, at the right edge of the field (the phone planner's
   * "add on the map" pin). Sits after the clear button.
   */
  trailingAction?: React.ReactNode;
  /**
   * 'large' — the planner's first field: 52px tall, prominent label, and
   * "Use my location" as a crosshair button inside the field instead of a
   * text link under it.
   * 'medium' — the same look at 44px for the fields that follow it:
   * "Finishing at", "Where to next?".
   * 'default' — 40px (the inline "add a stop here" box).
   */
  size?: 'default' | 'medium' | 'large';
  /** Muted text after the label, e.g. "optional". */
  labelHint?: string;
  /** Mark the field invalid and point at the message that explains why. */
  invalid?: boolean;
  describedBy?: string;
  /** Every keystroke, so a parent can tell "typed but not picked". */
  onTextChange?: (text: string) => void;
  /** The drawn road route, when there is one: rows say how far off it they
   *  are ("On your route", "3 km off route"). */
  route?: Anchor[] | null;
  /** Where a pick would land on the trip and what it adds ("Between
   *  Melbourne and Sydney", +100 km), per row. */
  placement?: (p: { lat: number; lng: number }) => PlacementInfo | null;
  /** Greyed out and inert (e.g. while an inline "add here" box is open). */
  disabled?: boolean;
  /** The suggestion list opened or closed (the host can quiet what's under it). */
  onOpenChange?: (open: boolean) => void;
}

/** Our own index — quick, so it can follow typing closely. */
const SEARCH_DEBOUNCE_MS = 220;
/** Nominatim — only once typing pauses (rate-limited upstream). */
const GEOCODE_DEBOUNCE_MS = 650;
const GEOCODE_MIN_CHARS = 3;

/* ── Icons (inline SVG, 16px, currentColor) ──────────────────────────────── */

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" {...props}>
    <circle cx="8.75" cy="8.75" r="5.25" />
    <path d="M12.75 12.75 16.5 16.5" />
  </svg>
);

function CrosshairGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}

const ClearIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" {...props}>
    <path d="M6 6l8 8M14 6l-8 8" />
  </svg>
);

const svgProps = {
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinejoin: 'round' as const,
  strokeLinecap: 'round' as const,
  'aria-hidden': true,
};

/** One glyph per kind of place, so a list of listings isn't a wall of
 *  identical tents. 20px grid, 1.6 stroke, currentColor. */
const ICON_PATHS: Record<PlaceIcon, React.ReactNode> = {
  // Town: a cluster of buildings.
  town: <path d="M3 16.5V9l4-2.5L11 9v7.5M11 16.5V5.5l3-2 3 2v11M2 16.5h16M6 12h2" />,
  // Park: a tree.
  park: <path d="M10 2.5 5 9.5h2.5L4.5 14h11l-3-4.5H15L10 2.5ZM10 14v3.5" />,
  // Place: a map pin.
  place: (
    <>
      <path d="M10 17.5s5.5-5 5.5-9.25a5.5 5.5 0 1 0-11 0C4.5 12.5 10 17.5 10 17.5Z" />
      <circle cx="10" cy="8.25" r="2" />
    </>
  ),
  // Region: a folded map.
  region: <path d="M2.5 5 7.5 3l5 2 5-2v12l-5 2-5-2-5 2V5ZM7.5 3v12M12.5 5v12" />,
  // Caravan park: a caravan.
  caravan: (
    <>
      <path d="M2 14V7a2 2 0 0 1 2-2h8.5A4.5 4.5 0 0 1 17 9.5V14H2ZM17 13h1.5" />
      <path d="M5 8h3v2.5H5zM11 8h2.5v6" />
      <circle cx="7.5" cy="14.5" r="1.5" />
    </>
  ),
  // Cabin / motel / cottage: a little house with a door.
  cabin: <path d="M3 9.5 10 3.5l7 6M4.5 8.3V16.5h11V8.3M8.5 16.5v-4.5h3v4.5" />,
  // Campground: a tent.
  tent: <path d="M10 3.5 2.5 16.5h15L10 3.5ZM10 16.5l-2.5-4.5h5L10 16.5Z" />,
  // Free camp: a campfire.
  free: (
    <>
      <path d="M10 3c1.8 2.2 3.5 3.8 3.5 6.2A3.5 3.5 0 0 1 10 12.7a3.5 3.5 0 0 1-3.5-3.5c0-1.4.7-2.4 1.6-3.2.2 1.2.8 1.9 1.6 2.2C9.4 6.6 9.5 4.8 10 3Z" />
      <path d="M4 17.5l12-3M16 17.5l-12-3" />
    </>
  ),
  // Rest area: a cup of coffee.
  rest: <path d="M4 8h9v4.5A4 4 0 0 1 9 16.5h-1A4 4 0 0 1 4 12.5V8ZM13 9.5h1.25a2 2 0 0 1 0 4H13M7 3.5v2M10 3.5v2M3 18h11" />,
};

/** Tile colours per kind: a tinted fill and a glyph colour that passes AA on it. */
const ICON_TILE: Record<PlaceIcon, string> = {
  town: 'bg-tp-brand-soft text-tp-brand ring-tp-brand/10',
  park: 'bg-tp-ok-soft text-tp-ok ring-tp-ok/10',
  place: 'bg-tp-surface text-tp-muted ring-tp-line',
  region: 'bg-tp-surface text-tp-muted ring-tp-line',
  caravan: 'bg-tp-warn-soft text-tp-warn ring-tp-warn-line',
  cabin: 'bg-tp-day-4/10 text-tp-day-4 ring-tp-day-4/15',
  tent: 'bg-tp-day-6/10 text-tp-day-6 ring-tp-day-6/15',
  free: 'bg-tp-accent-soft text-tp-accent ring-tp-accent/15',
  rest: 'bg-tp-surface text-tp-muted ring-tp-line',
};

function PlaceGlyph({ icon, className = 'h-[18px] w-[18px]' }: { icon: PlaceIcon; className?: string }) {
  return (
    <svg {...svgProps} className={className}>
      {ICON_PATHS[icon] ?? ICON_PATHS.place}
    </svg>
  );
}

/** Kinds of place you sleep at: these rows get the listing's photo. */
const STAY_ICONS = new Set<PlaceIcon>(['caravan', 'cabin', 'tent', 'free']);

/**
 * 36px leading visual. Places to stay (caravan parks, cabins, campgrounds)
 * show their own photo, with the type glyph in a small corner badge so the
 * kind still reads at a glance; towns, parks and regions keep a tinted
 * glyph tile. A photo that fails to load falls back to the tile.
 */
function RowVisual({ s }: { s: PlaceSuggestion }) {
  const icon: PlaceIcon = s.icon ?? 'place';
  const [broken, setBroken] = useState(false);
  if (s.image && STAY_ICONS.has(icon) && !broken) {
    return (
      <span aria-hidden="true" className="relative h-9 w-9 flex-none">
        <img
          src={s.image}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="h-9 w-9 rounded-tp-sm bg-tp-surface object-cover ring-1 ring-inset ring-black/5"
        />
        <span className={`absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-tp-sm ring-2 ring-white ${ICON_TILE[icon]}`}>
          <PlaceGlyph icon={icon} className="h-3 w-3" />
        </span>
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex h-9 w-9 flex-none items-center justify-center rounded-tp-sm ring-1 ring-inset ${ICON_TILE[icon]}`}
    >
      <PlaceGlyph icon={icon} />
    </span>
  );
}

/** Rows are a fixed height so the list can snap to whole rows. */
const ROW_PX = 52;
const VISIBLE_ROWS = 6;

/** Bold the part of the label that matches what was typed (Google-style). */
function Highlight({ text, query }: { text: string; query: string }) {
  const at = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return <span className="font-semibold">{text}</span>;
  return (
    <>
      {at > 0 && <span className="font-normal">{text.slice(0, at)}</span>}
      <span className="font-semibold">{text.slice(at, at + query.length)}</span>
      <span className="font-normal">{text.slice(at + query.length)}</span>
    </>
  );
}

export default function LocationAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  allowMyLocation,
  includeCampsites = true,
  near,
  autoFocus,
  onEscape,
  ariaLabel,
  route,
  hideLabel = false,
  placement,
  disabled,
  onOpenChange,
  leadingIcon,
  trailingAction,
  size = 'default',
  labelHint,
  invalid = false,
  describedBy,
  onTextChange,
}: LocationAutocompleteProps) {
  const large = size === 'large';
  const medium = size === 'medium';
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const hintId = `${inputId}-hint`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const geoAbortRef = useRef<AbortController | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Results for the CURRENT text only — a slow answer for "Mel" must not
  // land after the user has typed "Melbourne".
  const queryRef = useRef('');
  // Enter pressed before any suggestion arrived: add the top one when it does.
  const pendingEnterRef = useRef(false);

  const [text, setText] = useState<string>(value?.label ?? '');
  const [ownResults, setOwnResults] = useState<PlaceSuggestion[]>([]);
  const [geoResults, setGeoResults] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadingOwn, setLoadingOwn] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [geoUnavailable, setGeoUnavailable] = useState(false);
  const [ownDegraded, setOwnDegraded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable identity for the anchors so a parent re-render doesn't re-rank.
  const nearKey = (near ?? []).map((a) => `${a.lat.toFixed(2)},${a.lng.toFixed(2)}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const anchors = useMemo(() => near ?? [], [nearKey]);
  // Bias the geocoder towards the middle of the anchors.
  const centre = useMemo(() => {
    if (!anchors.length) return null;
    const lat = anchors.reduce((s, a) => s + a.lat, 0) / anchors.length;
    const lng = anchors.reduce((s, a) => s + a.lng, 0) / anchors.length;
    return { lat, lng };
  }, [anchors]);

  const q = text.trim();
  const suggestions = useMemo(
    () =>
      mergeSuggestions(
        includeCampsites ? ownResults : ownResults.filter((s) => s.kind !== 'campsite'),
        geoResults,
        8,
        q,
        anchors
      ),
    [includeCampsites, ownResults, geoResults, q, anchors]
  );

  // Keyed on the label rather than the object so a parent that rebuilds its
  // state object on every render doesn't wipe what the user is typing.
  useEffect(() => {
    setText(value?.label ?? '');
  }, [value?.label]);

  useEffect(() => {
    // preventScroll: the page may be mid-way through scrolling the planner on
    // screen (?plan=1); focusing must not yank it somewhere else.
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  // Close on an outside press. mousedown, not blur: blur fires before the click
  // on a result lands, which would tear the list down and swallow the pick.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the keyboard-highlighted row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const clearTimers = () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (geoTimerRef.current) clearTimeout(geoTimerRef.current);
  };

  useEffect(
    () => () => {
      clearTimers();
      searchAbortRef.current?.abort();
      geoAbortRef.current?.abort();
    },
    []
  );

  const runOwnSearch = useCallback(async (query: string) => {
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setLoadingOwn(true);
    const res = await fetchSearch(query, {
      types: includeCampsites ? ['location', 'campsite'] : ['location'],
      limit: 8,
      surface: 'trip-planner',
    });
    if (controller.signal.aborted || queryRef.current !== query) return;
    setLoadingOwn(false);
    setOwnDegraded(!!res.degraded);
    setOwnResults(fromSearchCards(res.results || []));
  }, [includeCampsites]);

  const runGeocode = useCallback(async (query: string) => {
    geoAbortRef.current?.abort();
    const controller = new AbortController();
    geoAbortRef.current = controller;
    setLoadingGeo(true);
    const { results, unavailable } = await geocodePlace(query, getCountryCode(), controller.signal, centre);
    if (controller.signal.aborted || queryRef.current !== query) return;
    setLoadingGeo(false);
    setGeoUnavailable(unavailable);
    setGeoResults(fromGeocode(results));
  }, [centre]);

  const onType = (next: string) => {
    setText(next);
    onTextChange?.(next);
    setError(null);
    setActive(0);
    clearTimers();
    pendingEnterRef.current = false;
    const query = next.trim();
    queryRef.current = query;
    // Clearing the box clears the resolved location, so the parent can't keep
    // routing from a place that is no longer shown.
    if (query === '') {
      // Nothing to look up any more: stop any lookup in flight, or its
      // spinner sits in an empty box.
      searchAbortRef.current?.abort();
      geoAbortRef.current?.abort();
      setLoadingOwn(false);
      setLoadingGeo(false);
      onChange(null);
      setOwnResults([]);
      setGeoResults([]);
      setOpen(false);
      return;
    }
    if (query.length < MIN_GEOCODE_QUERY) {
      searchAbortRef.current?.abort();
      geoAbortRef.current?.abort();
      setLoadingOwn(false);
      setLoadingGeo(false);
      setOwnResults([]);
      setGeoResults([]);
      return;
    }
    setOpen(true);
    // Drop geocoder rows for an older query — they'd describe a different place.
    setGeoResults([]);
    setLoadingOwn(true);
    searchTimerRef.current = setTimeout(() => void runOwnSearch(query), SEARCH_DEBOUNCE_MS);
    if (query.length >= GEOCODE_MIN_CHARS) {
      setLoadingGeo(true);
      geoTimerRef.current = setTimeout(() => void runGeocode(query), GEOCODE_DEBOUNCE_MS);
    }
  };

  const select = useCallback(
    async (s: PlaceSuggestion) => {
      clearTimers();
      pendingEnterRef.current = false;
      setOpen(false);
      setError(null);
      let lat = s.lat;
      let lng = s.lng;
      if ((typeof lat !== 'number' || typeof lng !== 'number') && s.resolveQuery) {
        // A town we know by name only — look its point up now. Show the pick
        // straight away (with the spinner) so the box doesn't look idle.
        setText(s.label);
        queryRef.current = s.label;
        setResolving(true);
        const { results } = await geocodePlace(s.resolveQuery, getCountryCode(), undefined, centre);
        setResolving(false);
        if (results[0]) {
          lat = results[0].lat;
          lng = results[0].lng;
        }
      }
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setError(`Couldn’t find “${s.label}” on the map. Try a nearby town.`);
        return;
      }
      setText(s.label);
      setOwnResults([]);
      setGeoResults([]);
      onChange({ label: s.label, lat, lng, entityId: s.entityId, kind: s.kind });
    },
    [onChange, centre]
  );

  // Enter beat the results: add the top one as soon as our index answers.
  useEffect(() => {
    if (!pendingEnterRef.current) return;
    if (suggestions.length && !loadingOwn) {
      void select(suggestions[0]);
    } else if (!loadingOwn && !loadingGeo) {
      pendingEnterRef.current = false; // nothing found — the list says so
    }
  }, [suggestions, loadingOwn, loadingGeo, select]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open && q.length >= MIN_GEOCODE_QUERY) {
        e.stopPropagation();
        setOpen(false);
      } else if (onEscape) {
        e.stopPropagation();
        onEscape();
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault(); // never submit the surrounding form
      const pick = loadingOwn ? null : suggestions[active] ?? suggestions[0];
      try {
        window.localStorage.setItem('tp-kbd-enter-used', '1');
      } catch {
        /* ignore */
      }
      if (pick) {
        void select(pick);
      } else if (q.length >= MIN_GEOCODE_QUERY) {
        // Typed fast and hit Enter: add the top result the moment it lands.
        pendingEnterRef.current = true;
        setOpen(true);
        if (!loadingOwn) {
          clearTimers();
          queryRef.current = q;
          void runOwnSearch(q);
          void runGeocode(q);
        }
      }
      return;
    }
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  const clearText = () => {
    onType('');
    inputRef.current?.focus();
  };

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setError('Location isn’t available in this browser.');
      return;
    }
    setBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: ResolvedLocation = {
          label: 'My current location',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          kind: 'place',
        };
        setText(loc.label);
        setOpen(false);
        onChange(loc);
        setBusy(false);
      },
      (err) => {
        setBusy(false);
        setError(err.message || 'Couldn’t get your location.');
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  // A town we know by name only has no point until it's picked. For its row
  // note, borrow the point of a listing in that town from the same results
  // ("Echo Beach Tourist Park · Lakes Entrance"): a few km either way doesn't
  // change which stop it goes after, and the town row no longer reads blank
  // between rows that all say where they'd go.
  const notePoint = (s: PlaceSuggestion): { lat?: number; lng?: number } => {
    if (typeof s.lat === 'number' && typeof s.lng === 'number') return s;
    if (s.placeType !== 'town') return {};
    const name = s.label.trim().toLowerCase();
    const twin = suggestions.find(
      (t) =>
        t !== s &&
        typeof t.lat === 'number' &&
        typeof t.lng === 'number' &&
        (!s.state || !t.state || s.state === t.state) &&
        (t.detail.toLowerCase().split(' · ').includes(name) || t.label.toLowerCase().startsWith(`${name} `))
    );
    return twin ? { lat: twin.lat, lng: twin.lng } : {};
  };

  // What each pick costs: a right-aligned chip ("+100 km", "On route"),
  // and for the highlighted row, in words in the footer ("Goes between
  // Melbourne and Sydney · about +1h 20m"). Distance off the current road
  // line made a town on the obvious coast road read as a 220 km mistake.
  const routeKey = route && route.length >= 2 ? `${route.length}:${route[0].lat.toFixed(3)}:${route[route.length - 1].lng.toFixed(3)}` : '';
  const costs = useMemo(() => {
    const m = new Map<string, PlacementInfo | null>();
    if (!placement) return m;
    for (const s of suggestions) {
      const pt = notePoint(s);
      if (typeof pt.lat !== 'number' || typeof pt.lng !== 'number') {
        m.set(s.key, null);
        continue;
      }
      const info = placement({ lat: pt.lat, lng: pt.lng });
      // Squared with the drawn road: beside it is on route; a few km up a
      // side road costs the out-and-back.
      const hug = routeKey && info ? nearestOnPath(route!, { lat: pt.lat, lng: pt.lng }) : null;
      m.set(s.key, withRoadDistance(info, hug?.km));
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, routeKey, placement]);
  const costOf = (c: PlacementInfo | null | undefined) =>
    !c ? null : c.onRoute ? 'On route' : formatAddedKm(c.addKm);
  const activeCost = suggestions[active] ? costs.get(suggestions[active].key) ?? null : null;
  // The keyboard legend is for learning: once Enter has added a stop, it
  // gets out of the way for good.
  const [kbdLearned, setKbdLearned] = useState(false);
  useEffect(() => {
    try {
      setKbdLearned(window.localStorage.getItem('tp-kbd-enter-used') === '1');
    } catch {
      /* private mode: keep the legend */
    }
  }, []);

  const loading = loadingOwn || loadingGeo;
  const showList = open && q.length >= MIN_GEOCODE_QUERY;
  const showSkeleton = showList && suggestions.length === 0 && loading;
  const activeId = showList && suggestions[active] ? `${listboxId}-${active}` : undefined;
  const openChangeRef = useRef(onOpenChange);
  openChangeRef.current = onOpenChange;
  useEffect(() => {
    openChangeRef.current?.(showList);
  }, [showList]);
  useEffect(() => () => openChangeRef.current?.(false), []);

  return (
    <div className={`space-y-1 ${showList ? 'relative z-40' : ''}`} ref={containerRef}>
      {/* Phones: a scrim behind the open list, so the stop rows under it
          don't read through or peek out below it. Tap it to close. */}
      {showList && (
        <div
          aria-hidden="true"
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen(false);
          }}
          className="fixed inset-0 z-0 bg-tp-ink/25 animate-tp-fade motion-reduce:animate-none md:hidden"
        />
      )}
      {label && (
        <label
          htmlFor={inputId}
          className={
            hideLabel
              ? 'sr-only'
              : large
                ? 'block text-tp-lg font-semibold tracking-tight text-tp-ink'
                : medium
                  ? 'block text-tp-base font-semibold text-tp-ink'
                  : 'block text-tp-sm font-medium text-tp-muted'
          }
        >
          {label}
          {labelHint && <span className="ml-1.5 text-tp-sm font-normal text-tp-muted">{labelHint}</span>}
        </label>
      )}
      <div className={`relative z-10 ${hideLabel || !label ? '' : large ? 'mt-2' : 'mt-1.5'}`}>
        {leadingIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 z-[1] flex -translate-y-1/2 items-center" aria-hidden="true">
            {leadingIcon}
          </span>
        ) : (
          <SearchIcon
            className={`pointer-events-none absolute top-1/2 z-[1] -translate-y-1/2 text-tp-faint ${
              large ? 'left-4 h-5 w-5' : 'left-3 h-[18px] w-[18px]'
            }`}
          />
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={label ? undefined : ariaLabel ?? placeholder}
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy ? `${hintId} ${describedBy}` : hintId}
          autoComplete="off"
          enterKeyHint="go"
          spellCheck={false}
          value={text}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => {
            if (suggestions.length) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          // The planner's field token (planner/ui FIELD_CLASS). Height/radius
          // inline: the storefront theme squares off bare inputs with a more
          // specific selector than a utility class. Large (52px, the
          // planner's first field), medium (44px, "Where to next?") and
          // default (40px, the inline "add a stop here" box) share the tokens.
          className={`box-border block w-full rounded-tp border border-solid bg-white text-tp-ink shadow-sm placeholder:text-tp-muted transition-[border-color,box-shadow] duration-150 ease-tp-out focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:border-tp-line disabled:bg-tp-surface disabled:shadow-none disabled:placeholder:text-tp-faint ${
            invalid
              ? 'border-tp-danger focus:border-tp-danger focus:ring-tp-danger/20'
              : 'border-tp-line-strong hover:border-tp-muted/60 focus:border-tp-brand focus:ring-tp-brand/25'
          } ${trailingAction ? (text ? 'pr-[84px]' : 'pr-12') : large ? (allowMyLocation ? 'pr-[88px]' : 'pr-11') : 'pr-10'} ${large ? 'pl-12' : 'pl-10'}`}
          style={{ height: large ? 52 : medium ? 44 : 40, borderRadius: 10, margin: 0, fontSize: large ? 16 : 15 }}
        />
        <span id={hintId} className="sr-only">
          Type a town, park or campsite. Use the arrow keys to choose and Enter to add it.
        </span>
        <span className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center">
          {loading || resolving || (large && busy) ? (
            <span
              aria-hidden="true"
              className="mx-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-tp-line border-t-tp-brand motion-reduce:animate-none"
            />
          ) : text && !disabled ? (
            <button
              type="button"
              onClick={clearText}
              aria-label="Clear"
              className="flex h-8 w-8 min-h-0 min-w-0 items-center justify-center rounded-tp-sm border-0 bg-transparent p-0 text-tp-faint transition-colors duration-150 hover:bg-tp-surface hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
            >
              <ClearIcon className="h-4 w-4" />
            </button>
          ) : null}
          {trailingAction}
          {large && allowMyLocation && (
            <button
              type="button"
              onClick={useMyLocation}
              disabled={busy || disabled}
              aria-label="Use my location"
              className="group/loc relative flex h-10 w-10 min-h-0 items-center justify-center rounded-tp border-0 bg-transparent text-tp-muted transition-colors duration-150 hover:bg-tp-brand-soft hover:text-tp-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 disabled:text-tp-faint"
            >
              <CrosshairGlyph className="h-5 w-5" />
              {/* A real tooltip (the native title is slow and invisible to
                  keyboard users): shows on hover and on keyboard focus. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-tp-sm bg-tp-ink px-2 py-1 text-tp-xs font-medium text-white opacity-0 shadow-tp-pop transition-opacity duration-150 group-hover/loc:opacity-100 group-focus-visible/loc:opacity-100 motion-reduce:transition-none"
              >
                Use my location
              </span>
            </button>
          )}
        </span>

        {/* An overlay, so opening/closing it never moves anything — an
            in-flow list collapsing after a pick slid the cards below under
            the cursor. */}
        {showList && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-tp border border-solid border-tp-line bg-white shadow-tp-pop animate-tp-fade motion-reduce:animate-none">
            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-label="Places"
              style={{ maxHeight: ROW_PX * VISIBLE_ROWS + 8 }}
              className="m-0 list-none overflow-y-auto overscroll-contain px-0 py-1"
            >
              {showSkeleton ? (
                [0, 1, 2].map((i) => (
                  <li key={i} className="flex h-[52px] items-center gap-3 px-3" aria-hidden="true">
                    <span className="h-9 w-9 flex-none animate-pulse rounded-tp-sm bg-tp-surface motion-reduce:animate-none" />
                    <span className="flex-1 space-y-1.5">
                      <span className="block h-3 w-2/5 animate-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                      <span className="block h-2.5 w-1/4 animate-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                    </span>
                  </li>
                ))
              ) : suggestions.length === 0 ? (
                <li className="px-4 py-3" role="none">
                  <p className="text-tp-base font-medium text-tp-ink">
                    {geoUnavailable && ownDegraded ? 'Place search is down for a moment' : `Nothing found for “${q}”`}
                  </p>
                  <p className="mt-0.5 text-tp-sm text-tp-muted">
                    {geoUnavailable && ownDegraded
                      ? 'Try again shortly, or drop a pin on the map.'
                      : 'Check the spelling, try a nearby town, or drop a pin on the map.'}
                  </p>
                </li>
              ) : (
                suggestions.map((s, i) => {
                  const isActive = i === active;
                  const cost = costs.get(s.key);
                  return (
                    <li
                      key={s.key}
                      id={`${listboxId}-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={isActive}
                      // Keep focus in the input; the click still lands because the
                      // list is inside containerRef and nothing closes on blur.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void select(s)}
                      onMouseMove={() => {
                        if (!isActive) setActive(i);
                      }}
                      className={`flex h-[52px] cursor-pointer items-center gap-3 pl-3 pr-2 transition-colors duration-75 ${
                        isActive ? 'bg-tp-brand-soft' : ''
                      }`}
                    >
                      <RowVisual s={s} />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-tp-base ${isActive ? 'text-tp-brand-ink' : 'text-tp-ink'}`}>
                          <Highlight text={s.label} query={q} />
                        </span>
                        <span className="block truncate text-tp-xs text-tp-muted">{s.detail}</span>
                      </span>
                      {/* What it adds to the drive, in one muted chip, in the
                          same column on every row. */}
                      {costOf(cost) && (
                        <span
                          className={`flex h-6 flex-none items-center whitespace-nowrap rounded-full px-2 text-tp-xs font-medium tabular-nums ${
                            cost?.onRoute ? 'bg-tp-ok-soft text-tp-ok' : 'bg-tp-surface text-tp-muted'
                          }`}
                        >
                          {costOf(cost)}
                        </span>
                      )}
                      {/* The highlighted row (Enter's pick) carries the "+";
                          the rest stay quiet. The whole row is the target. */}
                      <span aria-hidden="true" className="flex h-8 w-8 flex-none items-center justify-center">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-full bg-tp-brand text-white shadow-sm transition-[opacity,transform] duration-150 ease-tp-out ${
                            isActive ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                          }`}
                        >
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden="true">
                            <path d="M8 3.5v9M3.5 8h9" />
                          </svg>
                        </span>
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
            {suggestions.length > 0 ? (
              <div className="flex min-h-[36px] items-center justify-between gap-3 border-0 border-t border-solid border-tp-line bg-tp-surface px-3 py-1.5 text-tp-xs text-tp-muted">
                {/* Where the highlighted pick goes, in plain words. */}
                <span aria-live="polite" className="min-w-0 truncate">
                  {loadingGeo && !activeCost ? (
                    'Looking for parks and places…'
                  ) : activeCost ? (
                    <>
                      <span className="text-tp-ink">{activeCost.where}</span>
                      {!activeCost.onRoute && activeCost.addMin !== null && (
                        <span> · +{formatAddedDrive(activeCost.addMin)}</span>
                      )}
                    </>
                  ) : suggestions.length > VISIBLE_ROWS ? (
                    `${suggestions.length} places · scroll for ${suggestions.length - VISIBLE_ROWS} more`
                  ) : (
                    `${suggestions.length} place${suggestions.length === 1 ? '' : 's'}`
                  )}
                </span>
                {!kbdLearned && (
                  <span className="hidden flex-none items-center gap-1.5 [@media(hover:hover)]:flex" aria-hidden="true">
                    <kbd className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-tp-sm border border-solid border-tp-line-strong bg-white px-1.5 font-sans text-[11px] font-semibold text-tp-ink shadow-[0_1px_0_rgba(22,35,58,0.08)]">Enter</kbd>
                    <span>adds</span>
                  </span>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {allowMyLocation && !large && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
          <button
            type="button"
            onClick={useMyLocation}
            disabled={busy || disabled}
            className="inline-flex h-8 min-h-0 items-center gap-1.5 rounded-tp-sm border-0 bg-transparent px-1.5 text-tp-sm font-semibold text-tp-brand transition-colors duration-150 hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 disabled:text-tp-faint"
          >
            <CrosshairGlyph className="h-3.5 w-3.5" />
            {busy ? 'Finding you…' : 'Use my location'}
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="text-tp-xs text-tp-danger">
          {error}
        </p>
      )}
    </div>
  );
}
